import {CinematicVideoOverlay} from '@waka/compat/CinematicVideoOverlay';
import {MaskRenderBoundary,CinematicMaskedVideo} from '@waka/compat/CinematicMaskedMedia';
import {maskNeedsRendering} from '@waka/compat/cinematic-mask-effect';
import {sourceMediaRequest} from '@waka/compat/media-request';
import {BrowserRenderMediaVideo as MediaVideo} from '@waka/compat/browser-render-media';
import {BrowserAlphaVideo} from '@waka/compat/browser-alpha-video';
import {measureBrowserAlphaVideoDuration} from '@waka/compat/browser-alpha-video-duration';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loop,
  OffthreadVideo,
  useDelayRender,
  useRemotionEnvironment,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { type VideoObjectFit } from '@remotion/media';
import { parseMediaOnWebWorker } from '@remotion/media-parser/worker';
import { RequireCachedAsset } from '../../caching/require-cached-asset';
import { acquirePlaybackBoundaryDownloadPriority } from '../../caching/load-to-blob-url';
import {
  useAssetStartDelta,
  useIsInExtensionArea,
} from '../../canvas/transition-extension-context';
import { useStablePreferredLocalUrl } from '../../utils/find-asset-by-id';
import { useAssetFromItem, useDimensions } from '../../utils/use-context';
import { useReframeCrop } from './use-reframe-crop';
import { ReframeVideoRenderer } from './reframe-video-renderer';
import { volumeFn } from '../../utils/volume-fn';
import { useCroppableLayer } from '../croppable-layer';
import { ZoomWrapper } from '../zoom-wrapper';
import {
  calculateAnimationsStyle,
  mergeAnimationStyle,
} from '../animation/calculate-animation';
import {
  calculateFadeInOpacity,
  calculateFadeOutOpacity,
} from './calculate-fade';
import { AVATAR_SEGMENT_SOURCE_ROLE, VideoItem } from './video-item-type';
import { usePlaybackBoundaryReadinessReporter } from '../../canvas/playback-boundary-readiness';

const MEDIA_VIDEO_OBJECT_FIT = 'contain' satisfies VideoObjectFit;

/**
 * @remotion/media Video 래퍼
 */
const MediaVideoWithLoading = ({
  volume,
  trimBefore,
  src,
  innerStyle,
  objectFit,
  muted,
  playbackRate,
  loop,
  isCaptionClockSource,
}: {
  volume: (frame: number) => number;
  trimBefore: number;
  src: string;
  innerStyle: React.CSSProperties;
  objectFit: VideoObjectFit;
  muted: boolean;
  playbackRate: number;
  loop?: boolean;
  isCaptionClockSource?: boolean;
}) => {
  return (
    <MediaVideo
      requestInit={sourceMediaRequest}
      volume={volume}
      trimBefore={trimBefore}
      src={src}
      objectFit={objectFit}
      style={{
        ...innerStyle,
        objectFit: undefined,
        filter: 'brightness(1)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
      }}
      muted={muted}
      playbackRate={playbackRate}
      loop={loop === true}
      data-editor-caption-clock-source={
        isCaptionClockSource ? 'true' : undefined
      }
    />
  );
};

// 클립 실길이 측정 캐시 — 같은 카탈로그 클립을 쓰는 세그먼트가 많아 src 단위로
// 1회만 측정한다 (slowDurationInSeconds 는 파일 전체를 읽으므로 중복 측정이 비싸다).
const loopDurationCache = new Map<string, Promise<number | null>>();

const measureLoopDurationInSeconds = (src: string): Promise<number | null> => {
  const cached = loopDurationCache.get(src);
  if (cached) return cached;
  const promise = parseMediaOnWebWorker({
    src,
    fields: { slowDurationInSeconds: true },
    acknowledgeRemotionLicense: true,
  })
    .then((metadata) => {
      const seconds = metadata.slowDurationInSeconds;
      return typeof seconds === 'number' && seconds > 0 ? seconds : null;
    })
    .catch(() => {
      loopDurationCache.delete(src);
      return null;
    });
  loopDurationCache.set(src, promise);
  return promise;
};

/**
 * OffthreadVideo 는 loop 를 지원하지 않아 <Loop> 래퍼가 필요하고, Loop 는 클립
 * 실길이(반복 주기)를 알아야 한다. 클립 길이 메타가 없으므로(카탈로그 세그먼트
 * 클립) src 당 1회 측정한다. 측정 실패 시 loop 없이 렌더한다
 * (클립 종료 후 마지막 프레임 유지 — 세그먼트 잔여 ≤1s 수준의 완만한 폴백).
 *
 * delayRender 는 실제 렌더(export)에서만 건다 — 에디터 미리보기에서 걸면 측정
 * (파일 전체 읽기)이 끝날 때까지 진입이 블로킹된다. 미리보기는 논블로킹으로
 * 측정하고 완료되면 loop 로 전환된다.
 */
const useLoopDurationInFrames = (src: string, enabled: boolean, browserAlpha = false) => {
  const { fps } = useVideoConfig();
  const environment = useRemotionEnvironment();
  const {delayRender,continueRender} = useDelayRender();
  const [durationInFrames, setDurationInFrames] = useState<
    number | null | undefined
  >(enabled ? undefined : null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const controller=new AbortController();
    const isRendering = environment.isRendering || environment.isClientSideRendering;
    const handle = isRendering
      ? delayRender('measuring looped avatar clip duration')
      : null;
    const release = () => {
      if (handle !== null) continueRender(handle);
    };
    (browserAlpha ? measureBrowserAlphaVideoDuration(src,controller.signal).catch(()=>null) : measureLoopDurationInSeconds(src))
      .then((seconds) => {
        if (cancelled) return;
        setDurationInFrames(
          seconds !== null ? Math.max(1, Math.round(seconds * fps)) : null,
        );
      })
      .finally(release);
    return () => {
      cancelled = true;
      controller.abort();
      // 언마운트 시 pending 측정이 렌더를 붙잡지 않게 해제한다.
      // (finally 와 이중 호출돼도 continueRender 는 no-op — 안전)
      release();
    };
  }, [src, enabled, browserAlpha, fps, environment.isRendering, environment.isClientSideRendering, delayRender, continueRender]);

  return durationInFrames;
};

export const VideoLayer = ({
  item,
  trackMuted,
  cropBackground,
  zIndex,
  disableBoundaryMediaPause,
}: {
  item: VideoItem;
  trackMuted: boolean;
  cropBackground: boolean;
  zIndex?: number;
  disableBoundaryMediaPause?: boolean;
}) => {
  if (item.type !== 'video') {
    throw new Error('Item is not a video');
  }

  const frame = useCurrentFrame();
  const environment = useRemotionEnvironment();

  // 트랜지션 확장 영역에서는 오디오 음소거
  const isInExtensionArea = useIsInExtensionArea();
  // target 확장으로 인한 assetStart 조정값
  const assetStartDelta = useAssetStartDelta();
  const { fps } = useVideoConfig();

  const volume = useMemo(() => {
    return volumeFn({
      fadeWindow:item.audioFadeWindow,
      fps,
      audioFadeInDurationInSeconds: item.audioFadeInDurationInSeconds,
      audioFadeOutDurationInSeconds: item.audioFadeOutDurationInSeconds,
      durationInFrames: item.durationInFrames,
      decibelAdjustment: item.decibelAdjustment,
    });
  }, [
    item.audioFadeWindow,
    item.audioFadeInDurationInSeconds,
    item.audioFadeOutDurationInSeconds,
    item.decibelAdjustment,
    item.durationInFrames,
    fps,
  ]);

  const asset = useAssetFromItem(item);
  const isTransparentVideo = asset.type === 'video' && asset.hasAlpha === true;
  const isBoundaryAvatarSegment =
    !environment.isRendering && !environment.isClientSideRendering &&
    item.sourceRole === AVATAR_SEGMENT_SOURCE_ROLE &&
    item.hasAlpha === true &&
    isTransparentVideo;
  const src = useStablePreferredLocalUrl(asset, {
    keepMountedRemoteSource: isBoundaryAvatarSegment,
  });
  const isBlob = !environment.isClientSideRendering && src?.startsWith('blob:');
  const reportBoundaryReadiness = usePlaybackBoundaryReadinessReporter();
  const [hasDecodedBoundaryFrame, setHasDecodedBoundaryFrame] = useState(false);

  useEffect(() => {
    if (!isBoundaryAvatarSegment) return;
    setHasDecodedBoundaryFrame(false);
    reportBoundaryReadiness(item.id, false);
    return () => reportBoundaryReadiness(item.id, false);
  }, [isBoundaryAvatarSegment, item.id, reportBoundaryReadiness, src]);

  useEffect(() => {
    if (!isBoundaryAvatarSegment || hasDecodedBoundaryFrame) return;
    return acquirePlaybackBoundaryDownloadPriority(item.id);
  }, [hasDecodedBoundaryFrame, isBoundaryAvatarSegment, item.id]);

  const handleBoundaryVideoFrame = useCallback(() => {
    if (!isBoundaryAvatarSegment) return;
    setHasDecodedBoundaryFrame(true);
    reportBoundaryReadiness(item.id, true);
  }, [isBoundaryAvatarSegment, item.id, reportBoundaryReadiness]);
  // ProRes 중간본 등 @remotion/media가 디코딩 못 하는 비디오는 처음부터 OffthreadVideo 사용
  const forceOffthread =
    asset.type === 'video' && asset.forceOffthreadVideo === true;
  // 일반 영상은 캐시 전 MediaVideo와 캐시 후 OffthreadVideo의 레이아웃을 같게 유지한다.
  const isCachedStandardVideo =
    Boolean(isBlob) && !isTransparentVideo && !forceOffthread;
  // Browser slow export accepts declared-silent sources. Enforce that contract
  // even when retained media metadata is stale; never emit resampled source audio.
  const muted = trackMuted || isInExtensionArea || item.muteAudio === true ||
    (environment.isClientSideRendering && item.playbackRate < 1 &&
      asset.type === 'video' && asset.hasAudioTrack === false);

  // 리프레임 crop 계산
  const { compositionWidth, compositionHeight } = useDimensions();
  const reframeCrop = useReframeCrop(item, compositionWidth, compositionHeight,
    assetStartDelta, asset.type === 'video' ? {width: asset.width, height: asset.height} : undefined);

  const opacity = useMemo(() => {
    const inOpacity = calculateFadeInOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeInDurationInSeconds: item.fadeInDurationInSeconds,
      framesPerSecond: fps,
    });
    const outOpacity = calculateFadeOutOpacity({
      fadeWindow:item.visualFadeWindow,
      currentFrame: frame,
      fadeOutDurationInSeconds: item.fadeOutDurationInSeconds,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
    });
    return inOpacity * outOpacity * item.opacity;
  }, [
    item.visualFadeWindow,
    item.fadeInDurationInSeconds,
    fps,
    frame,
    item.opacity,
    item.durationInFrames,
    item.fadeOutDurationInSeconds,
  ]);

  const { innerStyle, outerStyle } = useCroppableLayer({
    item,
    rotation: item.rotation,
    opacity,
    borderRadius: item.borderRadius,
    cropBackground,
    zIndex,
  });

  // Animation 스타일 계산
  const animationStyle = useMemo(() => {
    const hasAnimations = item.animations && item.animations.length > 0;
    const hasPositionKeyframes =
      item.positionKeyframes && item.positionKeyframes.length > 0;
    if (!hasAnimations && !hasPositionKeyframes) {
      return {};
    }
    return calculateAnimationsStyle({
      animations: item.animations ?? [],
      currentFrame: frame,
      framesPerSecond: fps,
      totalDurationInFrames: item.durationInFrames,
      positionKeyframes: item.positionKeyframes,
    });
  }, [
    item.animations,
    item.positionKeyframes,
    frame,
    fps,
    item.durationInFrames,
  ]);

  // outerStyle에 animation 적용
  const finalOuterStyle = useMemo(() => {
    return mergeAnimationStyle(outerStyle, animationStyle);
  }, [outerStyle, animationStyle]);

  // target 확장이 있으면 assetStart를 앞으로 조정 (assetStartDelta는 음수)
  const startFrom = (item.videoStartFromInSeconds + assetStartDelta) * fps;

  const shouldLoop = item.loop === true;
  const isCaptionClockSource =
    item.isMainTrack === true &&
    item.from === 0 &&
    startFrom === 0 &&
    item.playbackRate === 1 &&
    !shouldLoop &&
    !muted;
  const loopDurationInFrames = useLoopDurationInFrames(
    src,
    shouldLoop && (!!isBlob || isTransparentVideo || forceOffthread),
    environment.isClientSideRendering && isTransparentVideo,
  );
  if(environment.isClientSideRendering && isTransparentVideo && shouldLoop && loopDurationInFrames===null)
    throw new Error('투명 영상의 반복 주기를 읽지 못했습니다. 원본 파일을 확인해 주세요.');

  if(item.cinematicMotion)return <div style={finalOuterStyle}><ZoomWrapper itemId={item.id} zoomKeyframes={cropBackground?undefined:item.zoomKeyframes} frame={frame}><RequireCachedAsset asset={asset}><MaskRenderBoundary label="영상 강조 표시"><CinematicVideoOverlay data={item.cinematicMotion} src={src} sourceUrl={asset.remoteUrl} sourceTime={startFrom/fps+frame/fps*item.playbackRate} frame={frame} fps={fps} style={innerStyle} muted={muted} volume={volume} trimBefore={startFrom} playbackRate={item.playbackRate} unsupported={!!reframeCrop||shouldLoop||isInExtensionArea} shouldBuffer={frame>=0&&frame<item.durationInFrames}/></MaskRenderBoundary></RequireCachedAsset></ZoomWrapper></div>;
  if(maskNeedsRendering(item.cinematicMasks))return (
    <div style={{...finalOuterStyle,backgroundColor:'#000'}}>
      <ZoomWrapper itemId={item.id} zoomKeyframes={cropBackground?undefined:item.zoomKeyframes} frame={frame}>
        <RequireCachedAsset asset={asset}>
          <MaskRenderBoundary key={JSON.stringify([item.cinematicMasks,asset.remoteUrl,!!reframeCrop,shouldLoop,isInExtensionArea])}>
            <CinematicMaskedVideo src={src} sourceUrl={asset.remoteUrl} masks={item.cinematicMasks}
              sourceTime={startFrom/fps+frame/fps*item.playbackRate} style={innerStyle}
              volume={volume} trimBefore={startFrom} playbackRate={item.playbackRate} muted={muted} captionClockSource={isCaptionClockSource} shouldBuffer={frame>=0&&frame<item.durationInFrames} unsupported={!!reframeCrop||shouldLoop||isInExtensionArea}/>
          </MaskRenderBoundary>
        </RequireCachedAsset>
      </ZoomWrapper>
    </div>
  );

  // 리프레임 활성 시 전용 렌더러 (finalOuterStyle로 감싸서 SelectionOutline과 동일한 위치/크기 보장)
  if (reframeCrop) {
    const videoWidth =
      item.reframeMeta?.videoWidth ?? item.sourceVideoWidth ??
      (asset.type === 'video' ? asset.width : compositionWidth);
    const videoHeight =
      item.reframeMeta?.videoHeight ??
      item.sourceVideoHeight ??
      (asset.type === 'video' ? asset.height : compositionHeight);

    return (
      <div style={finalOuterStyle}>
        <ZoomWrapper
          itemId={item.id}
          zoomKeyframes={cropBackground ? undefined : item.zoomKeyframes}
          frame={frame}
        >
          <ReframeVideoRenderer
            item={item}
            reframeCrop={reframeCrop}
            src={src}
            volume={volume}
            startFrom={startFrom}
            trackMuted={muted}
            isInExtensionArea={isInExtensionArea}
            compositionWidth={compositionWidth}
            compositionHeight={compositionHeight}
            isBlob={!!isBlob}
            transparent={isTransparentVideo}
            declaredPixels={asset.type === 'video' ? asset.width * asset.height : 0}
            hasAudioTrack={asset.type === 'video' && asset.hasAudioTrack !== false}
            forceOffthread={forceOffthread}
            playbackRate={item.playbackRate}
            sourceVideoWidth={videoWidth}
            sourceVideoHeight={videoHeight}
            disableBoundaryMediaPause={disableBoundaryMediaPause}
          />
        </ZoomWrapper>
      </div>
    );
  }

  const offthreadVideo = (
    <OffthreadVideo
      pauseWhenBuffering={!disableBoundaryMediaPause}
      crossOrigin={'anonymous' as const}
      useWebAudioApi
      volume={volume}
      trimBefore={startFrom}
      src={src}
      // ProRes 중간본(forceOffthread)은 항상 불투명 SDR 10-bit이므로 transparent(느린
      // 알파 경로)·toneMapped(불필요한 톤매핑)를 강제 off 한다. forceOffthreadVideo 는
      // MXF adapter 에서만 set 하므로 알파 WebM 아바타에는 영향이 없다.
      transparent={forceOffthread ? false : isTransparentVideo || undefined}
      toneMapped={forceOffthread ? false : undefined}
      style={{
        ...innerStyle,
        objectFit: isCachedStandardVideo
          ? MEDIA_VIDEO_OBJECT_FIT
          : innerStyle.objectFit,
        filter: 'brightness(1)',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
      }}
      muted={muted}
      playbackRate={item.playbackRate}
      onVideoFrame={
        isBoundaryAvatarSegment ? handleBoundaryVideoFrame : undefined
      }
    />
  );

  const alphaBrowserVideo = environment.isClientSideRendering && isTransparentVideo && !forceOffthread ? (
    <BrowserAlphaVideo src={src} trimBefore={startFrom} playbackRate={item.playbackRate}
      muted={muted} hasAudioTrack={asset.type==='video' && asset.hasAudioTrack !== false}
      declaredPixels={asset.type==='video' ? asset.width*asset.height : 0}
      volume={volume} isCaptionClockSource={isCaptionClockSource}
      style={{...innerStyle,objectFit:MEDIA_VIDEO_OBJECT_FIT,filter:'brightness(1)',
        WebkitBackfaceVisibility:'hidden',backfaceVisibility:'hidden'}}/>
  ) : null;

  return (
    <div style={finalOuterStyle}>
      <ZoomWrapper
        itemId={item.id}
        zoomKeyframes={cropBackground ? undefined : item.zoomKeyframes}
        frame={frame}
      >
        <RequireCachedAsset asset={asset}>
          {alphaBrowserVideo ? (
            shouldLoop && loopDurationInFrames ? (
              <Loop durationInFrames={loopDurationInFrames} layout="none">{alphaBrowserVideo}</Loop>
            ) : alphaBrowserVideo
          ) : isBlob || isTransparentVideo || forceOffthread ? (
            shouldLoop && loopDurationInFrames ? (
              <Loop
                durationInFrames={loopDurationInFrames}
                layout="none"
              >
                {offthreadVideo}
              </Loop>
            ) : (
              offthreadVideo
            )
          ) : (
            <MediaVideoWithLoading
              volume={volume}
              trimBefore={startFrom}
              src={src}
              innerStyle={innerStyle}
              objectFit={MEDIA_VIDEO_OBJECT_FIT}
              muted={muted}
              playbackRate={item.playbackRate}
              loop={shouldLoop}
              isCaptionClockSource={isCaptionClockSource}
            />
          )}
        </RequireCachedAsset>
      </ZoomWrapper>
    </div>
  );
};
